<template>
  <section>
    <div class="d-flex justify-content-end mb-1">
      <input
        ref="fileInput"
        type="file"
        class="d-none"
        @change="handleFileUpload"
      />
      <b-button
        variant="primary"
        size="sm"
        @click="triggerFileInput"
      >
        <GjIcon
          class="mr-50"
          name="Upload"
        />
        {{ $tk('ClientPortal.Form.UploadFile') }}
      </b-button>
    </div>

    <b-row
      v-if="loading"
      class="mx-n50"
    >
      <b-col
        v-for="i in 10"
        :key="i"
        class="px-50 mb-1"
        sm="6"
        md="4"
        lg="3"
      >
        <b-card>
          <b-skeleton-img />
          <b-skeleton
            animation="wave"
            width="60%"
            class="my-2"
          />
          <b-row class="mb-25">
            <b-col
              v-for="j in 4"
              :key="j"
              sm="3"
            >
              <b-skeleton
                animation="wave"
                width="100%"
              />
            </b-col>
          </b-row>
          <b-skeleton
            animation="wave"
            width="35%"
          />
          <b-skeleton
            animation="wave"
            width="65%"
          />
        </b-card>
      </b-col>
    </b-row>
    <b-row
      v-else
      class="mx-n50"
    >
      <b-col
        v-for="creative in creatives"
        :key="creative.id"
        class="px-50 mb-1"
        sm="6"
        md="4"
        lg="3"
      >
        <GalleryCreativesItem :creative="creative" />
      </b-col>

    </b-row>
    <InfiniteLoading
      :key="$route.params.accountId"
      spinner="spiral"
      @infinite="getNextCreatives($event)"
    >
      <div slot="no-results" />
      <div slot="no-more" />
    </InfiniteLoading>
  </section>
</template>

<script>
import GalleryService from '@/api/client/gallery.service'
import notification from '@/mixins/toastification'
import InfiniteLoading from 'vue-infinite-loading'
import UploadService from '@/api/client/upload.service'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import GalleryCreativesItem from './GalleryCreativesItem.vue'

export default {
  components: {
    GalleryCreativesItem,
    InfiniteLoading,
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      creatives: [],
      page: 0,
      loading: false,
      uploadMessage: '',
      allowedFormats: [],
    }
  },
  watch: {
    '$route.params.accountId': 'handleAccountChange',
  },
  async mounted() {
    await this.loadAllowedFormats()
    this.reloadGalleryCreatives()
  },
  methods: {
    async loadAllowedFormats() {
      try {
        this.allowedFormats = await GalleryService.getFormats(this.$route.params.accountId)
      } catch (error) {
        this.error(error.message)
      }
    },
    async handleAccountChange() {
      this.reloadGalleryCreatives()
      this.resetFileInput()
    },
    async reloadGalleryCreatives() {
      this.loading = true
      this.page = 0
      const filterModel = {
        page: this.page,
      }
      try {
        this.creatives = await GalleryService.getCreatives(filterModel, this.$route.params.accountId)
      } catch (error) {
        this.error(error.message)
      } finally {
        this.loading = false
      }
    },
    async getNextCreatives($state) {
      this.page += 1
      const filterModel = {
        page: this.page,
      }
      try {
        const creatives = await GalleryService.getCreatives(filterModel, this.$route.params.accountId)
        if (creatives.length) {
          this.creatives.push(...creatives)
          if ($state) $state.loaded()
        } else if ($state) $state.complete()
      } catch (error) {
        this.error(error.message)
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploadMessage = ''

      if (!this.isSupportedFileType(file.type)) {
        this.error(this.$tk('ClientPortal.Gallery.UnsupportedFileType'))
        return
      }

      const image = new Image()
      const reader = new FileReader()

      reader.onload = e => {
        image.src = e.target.result
        image.onload = async () => {
          const isValidFormat = this.fileFormatCheck(image.width, image.height)
          if (!isValidFormat) {
            this.error(this.$tk('ClientPortal.Gallery.WrongFormatUploaded'))
            return
          }

          const formData = new FormData()
          formData.append('file', file)

          try {
            const response = await UploadService.uploadImage(formData, this.$route.params.accountId)
            if (response.inserted) {
              const creativeModel = {
                fileName: response.fileName,
                formatId: response.formatId,
                imageLink: response.imageLink,
              }
              const creativeResponse = await GalleryService.createCreative(creativeModel, this.$route.params.accountId)
              if (creativeResponse.saved) {
                this.creatives.unshift(creativeResponse.creative)
                this.reloadGalleryCreatives()
                this.success(this.$tk('ClientPortal.Gallery.FileUploadedSuccessfully'))
              } else {
                this.error(this.$tk('ClientPortal.Gallery.ErrorInsertingFile'))
              }
            } else {
              this.error(this.$tk('ClientPortal.Gallery.ErrorUploadingFile'))
            }
          } catch (error) {
            this.uploadMessage = { success: false, message: error.message }
          }
        }
      }
      reader.readAsDataURL(file)
    },
    resetFileInput() {
      this.$refs.fileInput.value = null
    },
    fileFormatCheck(width, height) {
      return this.allowedFormats.some(format => format.width === width && format.height === height)
    },
    isSupportedFileType(fileType) {
      const supportedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp']
      return supportedTypes.includes(fileType)
    },
  },
}
</script>
